import type ProductDetails from '@dropins/storefront-pdp/containers/ProductDetails.js';
import { createModal, openModal } from 'blocks/modal/modal.ts';
import { buildCarousel } from 'blocks/shared/carousel/carousel.ts';
import { ensureHttps, getProductUrl } from 'blocks/shared/utils.ts';
import { fetchPlaceholders } from 'scripts/aem.js';
import type {
	ProductViewAttribute,
	ProductViewImage,
} from 'scripts/commerce/catalog-graphql.ts';
import { getSkuFromUrl } from 'scripts/commerce/utils.ts';
import { getConfigValue } from 'scripts/configs.ts';
import {
	button,
	div,
	h1,
	img,
	li,
	p,
	span,
	strong,
	ul,
} from 'scripts/dom-helpers.js';
import { getDomainConfigValue } from 'scripts/domain-configs.ts';
import {
	DIMENSION_IMAGE_PREFIX,
	PLP_CATEGORY,
	PRODUCT_LINE,
	TRANSMITTANCE_IMAGE_PREFIX,
} from 'shared/constants.ts';
import type { LinkDefinitions } from 'utils/fetchLinkDefinitions.ts';
import { fetchLinkDefinitions } from 'utils/fetchLinkDefinitions.ts';
import { loadErrorPage } from 'utils/loadErrorPage.ts';
import { getProduct } from '../../scripts/commerce/index.ts';
import type { CustomProductView } from '../../scripts/commerce/index.ts';
import type { ProductDetailsPlaceholders } from './product-details.placeholders.ts';
import { getProductDetailsPlaceholders } from './product-details.placeholders.ts';

import './product-details.css';
import './product-details.rebrand.css';

declare global {
	interface Window {
		getProductPromise: Promise<CustomProductView | null | undefined>;
	}
}

type ProductDetails = ReturnType<typeof mapProduct>;

const PATH_UPDATED = 'path-updated';
const PRODUCT_LINE_REDIRECT = 'product-line-redirect';

function mapProduct(
	product: CustomProductView,
	visibleProductAttributes: string[],
) {
	const filteredAttributes =
		product.attributes?.filter((att) => {
			return (
				att?.name && visibleProductAttributes.includes(att.name) && att.value
			);
		}) ?? [];

	// Dimension and transmittance images should be at the end of the list
	const images =
		product.images?.sort((a, b) => {
			const aName = a?.url?.split('/').pop() || '';
			const bName = b?.url?.split('/').pop() || '';

			const aIsSpecial =
				aName.startsWith(DIMENSION_IMAGE_PREFIX) ||
				aName.startsWith(TRANSMITTANCE_IMAGE_PREFIX);
			const bIsSpecial =
				bName.startsWith(DIMENSION_IMAGE_PREFIX) ||
				bName.startsWith(TRANSMITTANCE_IMAGE_PREFIX);

			if (aIsSpecial && !bIsSpecial) return 1;
			if (!aIsSpecial && bIsSpecial) return -1;
			return 0;
		}) ?? [];

	if (images.length === 0) {
		images.push({
			url: '/images/evident-logo.svg',
			label: 'Evident Logo',
			roles: [],
		});
	}

	return {
		sku: product.sku || '',
		name: product.pdpTitle || '',
		description: product.pdpLongDescription || '',
		shortDescription: product.pdpShortDescription,
		details: filteredAttributes as ProductViewAttribute[],
		images: images.map((image) => ({
			...image,
			url: ensureHttps(image?.url),
		})) as ProductViewImage[],
	};
}

async function handleProductLineRedirect(
	product: CustomProductView,
	searchParams: URLSearchParams,
) {
	// Look for parameter to prevent possible redirect loop
	const urlUpdateParam = searchParams.get(PRODUCT_LINE_REDIRECT);
	if (urlUpdateParam) {
		searchParams.delete(PRODUCT_LINE_REDIRECT);
		const url = new URL(window.location.href);
		url.search = searchParams.toString();
		window.history.replaceState({}, '', url.toString());
		return;
	}

	const productLineConfig = await getConfigValue('product-line');
	if (!productLineConfig) return;

	const productLineAttribute = product.attributes?.find(
		(attribute) => attribute?.name === PRODUCT_LINE,
	)?.value;

	if (!productLineAttribute || productLineConfig === productLineAttribute)
		return;

	const productsIndex = window.location.pathname.indexOf('/products/');
	if (productsIndex === -1) return;
	const productPath = window.location.pathname.slice(productsIndex + 10);

	switch (productLineAttribute) {
		case 'Life Science':
			{
				let lsProductUrl = await getDomainConfigValue('ls-product-url');
				if (lsProductUrl && productsIndex !== -1) {
					if (!lsProductUrl.endsWith('/')) lsProductUrl += '/';
					const newUrl = `${lsProductUrl}${productPath}?${PRODUCT_LINE_REDIRECT}=true`;
					window.location.replace(newUrl);
				}
			}
			break;
		case 'IMS':
			{
				let imsProductUrl = await getDomainConfigValue('ims-product-url');
				if (imsProductUrl && productsIndex !== -1) {
					if (!imsProductUrl.endsWith('/')) imsProductUrl += '/';
					window.location.replace(
						`${imsProductUrl}${productPath}?${PRODUCT_LINE_REDIRECT}=true`,
					);
				}
			}
			break;
	}
}

async function handleUrlUpdate(
	product: CustomProductView,
	linkDefinitions: LinkDefinitions,
) {
	const searchParams = new URLSearchParams(window.location.search);
	const urlUpdateParam = searchParams.get(PATH_UPDATED);
	if (urlUpdateParam) {
		searchParams.delete(PATH_UPDATED);
		const url = new URL(window.location.href);
		url.search = searchParams.toString();
		window.history.replaceState({}, '', url.toString());
	} else {
		const correctProductPath = getProductUrl(product, linkDefinitions);
		const currentPath = window.location.pathname;

		if (
			currentPath !== correctProductPath &&
			!currentPath.includes('/drafts/')
		) {
			// Include the parameter in the URL to prevent infinite redirects
			searchParams.set(PATH_UPDATED, 'true');
			window.location.replace(
				correctProductPath + '?' + searchParams.toString(),
			);
			return;
		}
	}

	// Handle products outside current site product line
	await handleProductLineRedirect(product, searchParams);
}

function buildDetailsList(
	details: ProductViewAttribute[],
	placeholders: ProductDetailsPlaceholders,
) {
	return div(
		{ class: 'product-details__details' },
		p(strong(placeholders.pdpProductDetails)),
		ul(
			...details.map((detail) => {
				const liElement = li();
				liElement.innerHTML = detail.label + ': ' + detail.value;
				return liElement;
			}),
		),
	);
}

function buildContentTop(
	product: ProductDetails,
	placeholders: ProductDetailsPlaceholders,
) {
	const contentTop = div(
		{ class: 'product-details__content-top' },
		div(
			{ class: 'product-details__sku' },
			`${placeholders.pdpSku}: ${product.sku}`,
		),
		h1({ class: 'product-details__title' }, product.name),
	);
	if (product.shortDescription) {
		const shortDescriptionElement = div({
			class: 'product-details__short-description',
		});
		shortDescriptionElement.innerHTML = product.shortDescription;
		contentTop.append(shortDescriptionElement);
	}
	if (product.description) {
		const descriptionElement = div({ class: 'product-details__description' });
		descriptionElement.innerHTML = product.description;
		contentTop.append(descriptionElement);
	}
	if (product.details?.length) {
		const detailsElement = buildDetailsList(product.details, placeholders);
		contentTop.append(detailsElement);
	}

	return contentTop;
}

function buildContentBottom(
	placeholders: ProductDetailsPlaceholders,
	pdpVariant: string,
) {
	const contactUsButton = button(
		{
			class: `button primary primary-variant-${pdpVariant}`,
			id: 'product-details__contact-us',
			type: 'button',
			onclick: async () => {
				await openModal(`${origin}/modals/forms/contact-us-product`);
			},
		},
		placeholders.pdpCustomContactUs,
	);
	const contentBottom = div(
		{ class: 'product-details__content-bottom' },
		contactUsButton,
	);
	if (pdpVariant !== 'default') {
		const requestDemoButton = button(
			{
				class: `button secondary variant-${pdpVariant}`,
				id: 'product-details__request-demo',
				type: 'button',
				onclick: async () => {
					await openModal(`${origin}/modals/forms/request-demo`);
				},
			},
			placeholders.pdpCustomRequestDemo,
		);
		contentBottom.append(requestDemoButton);
		const requestQuoteButton = button(
			{
				class: `button secondary variant-${pdpVariant}`,
				id: 'product-details__request-quote',
				type: 'button',
				onclick: async () => {
					await openModal(`${origin}/modals/forms/request-quote`);
				},
			},
			placeholders.pdpCustomRequestQuote,
		);
		contentBottom.append(requestQuoteButton);
	}
	return contentBottom;
}

function buildImages(
	images: ProductViewImage[],
	placeholders: ProductDetailsPlaceholders,
	product: ProductDetails,
) {
	const generateImageSrcset = (url: string) => {
		return `${url}?auto=webp&quality=80&crop=false&fit=cover&width=384 768w,
	${url}?auto=webp&quality=80&crop=false&fit=cover&width=512 1024w,
	${url}?auto=webp&quality=80&crop=false&fit=cover&width=683 1366w,
	${url}?auto=webp&quality=80&crop=false&fit=cover&width=960 1920w`;
	};

	const openImageModal = async (image: ProductViewImage, imageAlt: string) => {
		const modalImage = img({
			src: image.url,
			alt: imageAlt,
			class: 'product-details__image-modal',
		});

		const { showModal } = await createModal({
			contentNodes: [modalImage],
			className: 'image-modal',
		});
		showModal();
	};

	return images.map((image, index) => {
		const imageAlt = placeholders.pdpProductImage
			.replace('{product}', image.label || product.name)
			.replace('{key}', (index + 1).toString())
			.replace('{total}', images.length.toString());
		const imgElement = img({
			src: image.url,
			srcset: generateImageSrcset(image.url),
			alt: imageAlt,
			class: image.label === 'Evident Logo' ? 'evident-logo' : '',
			loading: 'eager',
			style: 'cursor: pointer;',
			role: 'button',
			tabindex: '0',
			'aria-label': imageAlt,
		});

		const openModal = () => {
			void openImageModal(image, imageAlt);
		};

		imgElement.addEventListener('click', openModal);
		imgElement.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				openModal();
			}
		});

		if (!imgElement.complete) {
			imgElement.classList.add('loading');
			imgElement.addEventListener('load', () => {
				imgElement.classList.remove('loading');
			});
		}
		return imgElement;
	});
}

function buildProductDetails(
	product: ProductDetails,
	placeholders: ProductDetailsPlaceholders,
	block: HTMLElement,
	pdpVariant: string,
) {
	const contentColumn = div(
		{ class: 'product-details__content-column' },
		buildContentTop(product, placeholders),
		buildContentBottom(placeholders, pdpVariant),
	);

	// Duplicate the content column to allow proper tabindex on page
	const contentColumnClone = contentColumn.cloneNode(true) as HTMLElement;
	contentColumnClone.querySelectorAll('button').forEach((clonedButton) => {
		const originalId = clonedButton.id;
		// Remove the id from the cloned button
		clonedButton.removeAttribute('id');
		switch (originalId) {
			case 'product-details__contact-us':
				clonedButton.addEventListener('click', () => {
					void openModal(`${origin}/modals/forms/contact-us-product`);
				});
				break;
			case 'product-details__request-demo':
				clonedButton.addEventListener('click', () => {
					void openModal(`${origin}/modals/forms/request-demo`);
				});
				break;
			case 'product-details__request-quote':
				clonedButton.addEventListener('click', () => {
					void openModal(`${origin}/modals/forms/request-quote`);
				});
				break;
			default:
				break;
		}
	});
	contentColumn.classList.add('before-image');
	contentColumnClone.classList.add('after-image');

	const images = buildImages(product.images, placeholders, product);
	const imageColumn = div({ class: 'product-details__image-column' });
	if (images.length > 1) {
		buildCarousel(images, imageColumn, true, placeholders);
	} else {
		imageColumn.append(...images);
		imageColumn.append(
			span({ class: 'product-details__mobile-image-gradient' }),
		);
	}

	block.innerHTML = '';
	block.append(contentColumn, imageColumn, contentColumnClone);
}

export default async function decorate(block: HTMLElement) {
	const productPromise =
		window.getProductPromise ?? getProduct(getSkuFromUrl(true));

	const [
		product,
		rawPlaceholders,
		linkDefinitions,
		visibleProductAttributeCodes,
	] = await Promise.all([
		productPromise,
		fetchPlaceholders(),
		fetchLinkDefinitions(),
		getConfigValue('visible-product-attribute-codes'),
	]);

	if (!product) {
		await loadErrorPage();
		return;
	}

	const placeholders = getProductDetailsPlaceholders(rawPlaceholders);

	await handleUrlUpdate(product, linkDefinitions);

	const visibleProductAttributes =
		visibleProductAttributeCodes
			?.split(',')
			.map((attribute) => attribute.trim()) ?? [];
	const productDetails = mapProduct(product, visibleProductAttributes);
	if (document.title.toLowerCase() === 'product-details') {
		document.title = productDetails.name;
	}

	// Set PLP_CATEGORY in sessionStorage for use in breadcrumbs-share
	const plpCategory = product.attributes?.find(
		(att) => att?.name === PLP_CATEGORY,
	)?.value;
	if (plpCategory && typeof plpCategory === 'string')
		sessionStorage.setItem(PLP_CATEGORY, plpCategory);

	const classes = ['two-col', 'three-col', 'stacked'];
	const pdpVariant =
		classes.find((cls) => block.classList.contains(cls)) || 'default';

	buildProductDetails(productDetails, placeholders, block, pdpVariant);
}
